import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseApiError } from "../../lib/http/apiErrors";
import ConfirmModal from "../../components/admin/ConfirmModal";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useCountries } from "../../features/geo/hooks";
import AdminOnboardingOptionsPage from "./AdminOnboardingOptionsPage";
import AdminSellerTypesPage from "./AdminSellerTypesPage";
import { FlowFieldRenderer } from "../../features/onboardingFlows/FieldRenderer";
import {
  onboardingFlowsService,
  type FieldConfig,
  type FieldType,
  type FlowDoc,
  type FlowRole,
  type StepConfig,
} from "../../features/onboardingFlows/service";
import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../../features/admin/service";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

const DEFAULT_COUNTRY = "DEFAULT";

const FIELD_TYPES: FieldType[] = ["text", "textarea", "number", "select", "multiselect", "checkbox", "yesno", "upload", "phone", "offices", "multiple", "date", "email", "currency_number", "gst"];

/** Plain-language explanation of what each field type means and how it's submitted — shown to admins building a flow so they don't have to read the backend's FieldType docstring. */
const FIELD_TYPE_DESCRIPTIONS: Record<FieldType, string> = {
  text: "Single-line free text.",
  textarea: "Multi-line free text.",
  number: "Numeric input.",
  select: "One choice from an admin-managed option set (pick an Options type below).",
  multiselect: "Multiple choices from an admin-managed option set.",
  checkbox: "A single true/false toggle.",
  yesno: "A yes/no choice (distinct from checkbox — renders as two buttons).",
  upload: "A document/file upload; stores the uploaded file's key.",
  phone: "A phone number with country calling code, stored as two linked values.",
  offices: "One or more office/business addresses (country, state, city, address, pincode).",
  multiple: "An admin-defined repeatable group — pick which sub-fields each repeated entry can hold.",
  group: "A fixed set of sub-fields shown together as one unit.",
  date: "A calendar date.",
  email: "An email address, validated for basic format.",
  currency_number: "An amount with a selectable currency code, stored as two linked values.",
  gst: "Repeatable GST/VAT registration entries. For GST: the number is live-verified and the company name auto-fills and locks to the verified legal name, with one office pre-filled from the registered address; brand name stays editable, and more offices can be added. For VAT: all fields (number, company name, brand name, offices) are free entry — no verification service exists for VAT yet.",
};

/** What shape of value the user's answer is submitted as, per FieldType — mirrors the FieldType docstring in backend/app/modules/onboarding/flows.py so admins don't have to cross-reference the backend. */
const FIELD_TYPE_ACCEPTS: Record<FieldType, string> = {
  text: "string",
  textarea: "string (multi-line)",
  number: "string | number",
  select: "string — one of the option set's values",
  multiselect: "string[]",
  checkbox: "boolean",
  yesno: '"yes" | "no"',
  upload: "string — S3 key from the upload endpoint",
  phone: "TWO keys: number (no \"+\") + <key>_country_code (e.g. \"+91\")",
  offices: "array of {country, state, city, address, pincode, is_main, ...}",
  multiple: "array of {sub_field_key: value} — shape set by this field's sub-fields",
  group: "object of {sub_field_key: value} — one entry per sub-field",
  date: "string (ISO date)",
  email: "string (validated)",
  currency_number: "TWO keys: amount + <key>_currency (3-letter code, e.g. \"USD\")",
  gst: "array of {tax_type: \"GST\"|\"VAT\", tax_number, company_name, brand_name, offices: [...]}",
};

type Tab = "seller" | "buyer" | "partner" | "options" | "seller_types" | "field_types";

const TABS: { key: Tab; label: string }[] = [
  { key: "seller", label: "Sellers" },
  { key: "buyer", label: "Buyers" },
  { key: "partner", label: "Partners" },
  { key: "options", label: "Option Sets" },
  { key: "seller_types", label: "Seller Types" },
  { key: "field_types", label: "Field Types" },
];

/** Reference tab: every FieldType an admin can pick, in a readable table. */
function FieldTypesReferencePage() {
  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6">
      <h2 className="text-lg font-semibold text-white mb-1">Field Types Reference</h2>
      <p className="text-sm text-gray-400 mb-5">
        What each field type means, and the exact shape a user's answer is submitted as — for anyone building or editing an onboarding flow.
      </p>
      <div className="space-y-3">
        {FIELD_TYPES.map((t) => (
          <div key={t} className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block px-2 py-0.5 rounded bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 font-mono text-xs font-semibold">
                {t}
              </span>
            </div>
            <p className="text-sm text-gray-200 leading-relaxed mb-2">{FIELD_TYPE_DESCRIPTIONS[t]}</p>
            <div className="flex items-start gap-2 text-xs">
              <span className="text-gray-500 uppercase tracking-wider flex-shrink-0 pt-0.5">Accepts</span>
              <code className="text-gray-300 bg-gray-900/70 border border-gray-700/50 rounded px-2 py-0.5 font-mono leading-relaxed">
                {FIELD_TYPE_ACCEPTS[t]}
              </code>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Small click-to-open help popover, styled for this page's hardcoded dark
 * palette (the shared InfoTip component uses theme tokens that don't
 * resolve correctly on this page's fixed gray-900 background). */
function TypeHelpTip({ type }: { type: FieldType }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label={`What does "${type}" do?`}
        onClick={() => setOpen((v) => !v)}
        className="h-3.5 w-3.5 flex-shrink-0 rounded-full border border-gray-500 text-gray-500 text-[9px] leading-[13px] text-center hover:border-indigo-400 hover:text-indigo-400"
      >
        ?
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-xl border border-gray-700 bg-gray-900 p-3 shadow-lg"
        >
          <p className="mb-1 text-xs font-mono font-semibold text-indigo-300">{type}</p>
          <p className="text-xs leading-relaxed text-gray-300 mb-2">{FIELD_TYPE_DESCRIPTIONS[type]}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Accepts</p>
          <code className="block text-[10px] text-gray-400 font-mono leading-relaxed">{FIELD_TYPE_ACCEPTS[type]}</code>
          <div className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r border-gray-700 bg-gray-900" />
        </div>
      )}
    </div>
  );
}

function countryLabel(country: string, countryName?: string): string {
  if (country === DEFAULT_COUNTRY) return "Default (fallback)";
  return countryName ? `${countryName} (${country})` : country;
}

function AdminFieldList({ 
  fields, 
  onChange, 
  optionTypes,
  deriveKeyFromLabel,
  inputCls 
}: { 
  fields: FieldConfig[]; 
  onChange: (fields: FieldConfig[]) => void;
  optionTypes: string[];
  deriveKeyFromLabel: (label: string) => string;
  inputCls: string;
}) {
  const updateField = (index: number, patch: Partial<FieldConfig>) => {
    onChange(fields.map((f, i) => i === index ? { ...f, ...patch } : f));
  };
  const addField = () => {
    onChange([...fields, { key: "", label: "", type: "text", required: false, options_type: null, info_tip: "" }]);
  };
  const removeField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  };
  const moveField = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {fields.map((field, index) => {
        const showOptions = field.type === "select" || field.type === "multiselect";
        const isMultiple = field.type === "multiple";
        return (
          <div key={index} className="flex flex-col gap-2 bg-gray-800/40 rounded-lg px-3 py-2">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-[10px] text-gray-500 mb-1">Key</label>
                <input
                  value={field.key}
                  onChange={(e) => updateField(index, { key: e.target.value })}
                  placeholder="field_key"
                  className={inputCls}
                />
              </div>
              <div className="flex-1">
                <label className="block text-[10px] text-gray-500 mb-1">Label</label>
                <input
                  value={field.label}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    const patch: any = { label: newLabel };
                    if (!field.key || field.key === deriveKeyFromLabel(field.label)) {
                      patch.key = deriveKeyFromLabel(newLabel);
                    }
                    updateField(index, patch);
                  }}
                  placeholder="Label"
                  className={inputCls}
                />
              </div>
              <div className="w-32">
                <label className="flex items-center gap-1 text-[10px] text-gray-500 mb-1">
                  Type
                  <TypeHelpTip type={field.type} />
                </label>
                <select
                  value={field.type}
                  onChange={(e) =>
                    updateField(index, { type: e.target.value as FieldType })
                  }
                  className={inputCls}
                >
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t} title={FIELD_TYPE_DESCRIPTIONS[t]}>{t}</option>
                  ))}
                </select>
              </div>
              {showOptions && (
                <div className="flex-1">
                  <label className="block text-[10px] text-gray-500 mb-1">Options type</label>
                  <select
                    value={field.options_type ?? ""}
                    onChange={(e) =>
                      updateField(index, { options_type: e.target.value || null })
                    }
                    className={inputCls}
                  >
                    <option value="">Select option set...</option>
                    {optionTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex-1">
                <label className="block text-[10px] text-gray-500 mb-1">Info Tip</label>
                <input
                  value={field.info_tip ?? ""}
                  onChange={(e) => updateField(index, { info_tip: e.target.value })}
                  placeholder="Tooltip..."
                  className={inputCls}
                />
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap pb-2">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => updateField(index, { required: e.target.checked })}
                  className="accent-indigo-500"
                />
                Req
              </label>
              <div className="flex flex-col gap-1 pb-1">
                <button
                  type="button"
                  onClick={() => moveField(index, -1)}
                  disabled={index === 0}
                  className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs disabled:opacity-30"
                >↑</button>
                <button
                  type="button"
                  onClick={() => moveField(index, 1)}
                  disabled={index === fields.length - 1}
                  className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs disabled:opacity-30"
                >↓</button>
              </div>
              <button
                type="button"
                onClick={() => removeField(index)}
                className="px-3 py-2 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors"
              >✕</button>
            </div>
            
            {isMultiple && (
              <div className="ml-8 mt-2 p-3 bg-gray-900/50 rounded border border-gray-700/50">
                <h4 className="text-xs font-semibold text-gray-400 mb-2">Sub-fields for "{field.label || 'Group'}"</h4>
                <AdminFieldList
                  fields={field.sub_fields || []}
                  onChange={(newSubFields) => updateField(index, { sub_fields: newSubFields })}
                  optionTypes={optionTypes}
                  deriveKeyFromLabel={deriveKeyFromLabel}
                  inputCls={inputCls}
                />
              </div>
            )}
          </div>
        );
      })}
      <div className="mt-2">
        <button
          type="button"
          onClick={addField}
          className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-xs text-gray-300 transition-colors inline-block text-left max-w-max"
        >
          + Add field
        </button>
      </div>
    </div>
  );
}


function reorderSteps(steps: StepConfig[]): StepConfig[] {
  return steps.map((s, i) => ({ ...s, order: i }));
}

function stampOriginalKeys(steps: StepConfig[]): StepConfig[] {
  return steps.map(s => ({
    ...s,
    _originalKey: s._originalKey || s.key,
    fields: s.fields.map(f => ({ ...f, _originalKey: f._originalKey || f.key }))
  }));
}

interface FlowBuilderProps {
  role: FlowRole;
}

function FlowBuilder({ role }: FlowBuilderProps) {
  const { countries } = useCountries();
  const countryName = useCallback(
    (iso2: string) => countries.find((c) => c.iso2 === iso2)?.name,
    [countries],
  );

  const [docs, setDocs] = useState<FlowDoc[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>(DEFAULT_COUNTRY);
  const [steps, setSteps] = useState<StepConfig[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  useBodyScrollLock(showPreview);
  const [previewStepIndex, setPreviewStepIndex] = useState(0);
  const [success, setSuccess] = useState<string | null>(null);
  const { showError, showSuccess } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  useEffect(() => { if (success) showSuccess(success); }, [success, showSuccess]);
  const stepsEndRef = useRef<HTMLDivElement>(null);

  // Add-country-config controls
  const [newCountryIso2, setNewCountryIso2] = useState("");
  const [newCountryInput, setNewCountryInput] = useState("");
  const [newSourceIso2, setNewSourceIso2] = useState<string>(DEFAULT_COUNTRY);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // New-step controls
  const [newStepLabel, setNewStepLabel] = useState("");
  const [newStepKey, setNewStepKey] = useState("");

  // Selectable option types state
  const [optionTypes, setOptionTypes] = useState<string[]>([]);

  const existingCountries = useMemo(
    () => docs.map((d) => d.country).filter((c) => c !== DEFAULT_COUNTRY).sort(),
    [docs],
  );

  // Derive keys from labels
  const deriveKeyFromLabel = (lbl: string) => {
    return lbl
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  useEffect(() => {
    // The selectable option sets are driven by what actually exists in
    // onboarding_options (admin-managed). Geo sets (countries/states/cities)
    // are not stored there — they come from the geo dataset — so they're the
    // only types we add on top. No hardcoded option-set names: anything an
    // admin creates shows up automatically, and nothing phantom is offered.
    const GEO_OPTION_TYPES = ["countries", "states", "cities"];
    apiClient.get("/api/v1/onboarding/admin/options", {
      headers: { Authorization: `Bearer ${adminStorage.getToken()}` }
    })
      .then(({ data }) => {
        const dbTypes = (data || []).map((o: any) => o.type).filter(Boolean);
        const combined = Array.from(new Set([...GEO_OPTION_TYPES, ...dbTypes])).sort();
        setOptionTypes(combined);
      })
      .catch(() => {
        setOptionTypes([...GEO_OPTION_TYPES]);
      });
  }, []);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await onboardingFlowsService.list(role);
      setDocs(list);
      return list;
    } catch (err) {
      setError(parseApiError(err).message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [role]);

  const loadCountry = useCallback(
    async (country: string) => {
      setError(null);
      try {
        const doc = await onboardingFlowsService.get(role, country);
        setSteps(stampOriginalKeys([...doc.steps].sort((a, b) => a.order - b.order)));
        setSelectedCountry(country);
        setExpanded(new Set());
      } catch (err) {
        setError(parseApiError(err).message);
      }
    },
    [role],
  );

  // Initial load: fetch list then the DEFAULT doc.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await onboardingFlowsService.list(role).catch((err) => {
        if (!cancelled) setError(parseApiError(err).message);
        return [] as FlowDoc[];
      });
      if (cancelled) return;
      setDocs(list);
      const target = list.find((d) => d.country === DEFAULT_COUNTRY) ?? list[0];
      if (target) {
        setSteps(stampOriginalKeys([...target.steps].sort((a, b) => a.order - b.order)));
        setSelectedCountry(target.country);
        setExpanded(new Set());
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [role]);

  // ── Step mutations ────────────────────────────────────────────────────────

  const updateStep = (index: number, patch: Partial<StepConfig>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeStep = (index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAddStep = () => {
    const label = newStepLabel.trim();
    if (!label) {
      setError("Step label is required.");
      return;
    }
    const key = newStepKey.trim() || deriveKeyFromLabel(label);
    if (!key) {
      setError("Could not derive valid key from label.");
      return;
    }
    if (steps.some((s) => s.key === key)) {
      setError(`A step with key "${key}" already exists.`);
      return;
    }
    setError(null);
    const newStep: StepConfig = {
      key,
      label,
      title: label,
      description: "",
      order: steps.length,
      required: false,
      fields: [],
    };
    setSteps((prev) => [...prev, newStep]);
    setExpanded((prev) => new Set(prev).add(key));
    setNewStepLabel("");
    setNewStepKey("");
    
    setTimeout(() => {
      stepsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  };



  // ── Actions ───────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = reorderSteps(steps);
      const keyChanges: any[] = [];
      for (const s of payload) {
        if (s._originalKey && s._originalKey !== s.key) {
          keyChanges.push({ type: "step", old_key: s._originalKey, new_key: s.key });
        }
      }
      for (const s of payload) {
        for (const f of s.fields) {
          if (f._originalKey && f._originalKey !== f.key) {
            keyChanges.push({ type: "field", old_key: f._originalKey, new_key: f.key, step_key: s.key });
          }
        }
      }
      const doc = await onboardingFlowsService.upsert(role, selectedCountry, payload, keyChanges);
      setSteps(stampOriginalKeys([...doc.steps].sort((a, b) => a.order - b.order)));
      setSuccess(`Saved ${countryLabel(selectedCountry, countryName(selectedCountry))}.`);
      await loadDocs();
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOverride = async () => {
    const iso2 = newCountryIso2.trim().toUpperCase();
    if (!iso2) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const doc = await onboardingFlowsService.copy(role, newSourceIso2, iso2);
      setNewCountryIso2("");
      await loadDocs();
      setSteps(stampOriginalKeys([...doc.steps].sort((a, b) => a.order - b.order)));
      setSelectedCountry(doc.country);
      setExpanded(new Set());
      setSuccess(`Created config for ${countryLabel(iso2, countryName(iso2))} from ${countryLabel(newSourceIso2, countryName(newSourceIso2))}.`);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    if (selectedCountry === DEFAULT_COUNTRY) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await onboardingFlowsService.remove(role, selectedCountry);
      await loadDocs();
      await loadCountry(DEFAULT_COUNTRY);
      setSuccess("Config deleted. Showing Default config.");
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const inputCls =
    "w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50";

  return (
    <div>
      {role === "partner" && (
        <div className="mb-4 px-4 py-3 bg-indigo-900/30 border border-indigo-700/50 rounded-xl text-indigo-200 text-xs">
          Partner onboarding has no live signup flow yet — this configures step/field definitions for
          future use.
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 px-4 py-3 bg-green-900/40 border border-green-700 rounded-xl text-green-300 text-sm">{success}</div>
      )}

      {/* Country selector + controls */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-white">Country configuration</h2>
          <RefreshButton
            onRefresh={async () => {
              await loadDocs();
              await loadCountry(selectedCountry);
            }}
          />
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            type="button"
            onClick={() => void loadCountry(DEFAULT_COUNTRY)}
            className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
              selectedCountry === DEFAULT_COUNTRY
                ? "bg-indigo-600 text-white"
                : "bg-gray-800 hover:bg-gray-700 text-gray-300 border border-dashed border-gray-600"
            }`}
          >
            Default (fallback)
          </button>
          {existingCountries.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => void loadCountry(c)}
              className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                selectedCountry === c ? "bg-indigo-600 text-white" : "bg-gray-800 hover:bg-gray-700 text-gray-300"
              }`}
            >
              {countryLabel(c, countryName(c))}
            </button>
          ))}
        </div>

        <div className="mb-8 p-6 bg-gray-800/40 border border-gray-700/60 rounded-xl">
          <label className="block text-sm font-semibold text-white mb-3">Add new country config</label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              list="admin-country-list"
              value={newCountryInput}
              onChange={(e) => {
                setNewCountryInput(e.target.value);
                const match = countries.find(c => `${c.name} (${c.iso2})` === e.target.value);
                setNewCountryIso2(match ? match.iso2 : "");
                setNewSourceIso2(DEFAULT_COUNTRY); // Always copy from default
              }}
              placeholder="Search or select country..."
              className={`${inputCls} max-w-xs`}
            />
            <datalist id="admin-country-list">
              {countries.map((c) => (
                <option key={c.iso2} value={`${c.name} (${c.iso2})`} />
              ))}
            </datalist>

            <button
              type="button"
              onClick={() => void handleCreateOverride()}
              disabled={!newCountryIso2 || busy}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap ml-auto"
            >
              Create Config
            </button>
          </div>
        </div>

        {selectedCountry !== DEFAULT_COUNTRY && (
          <div className="mt-5 pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="px-4 py-2 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors disabled:opacity-50"
            >
              Delete this country config
            </button>
            <span className="text-gray-500 text-xs ml-3">Falls back to Default once removed.</span>
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white">
            Steps — {countryLabel(selectedCountry, countryName(selectedCountry))}
            <span className="text-gray-500 font-normal ml-1">({steps.length})</span>
          </h2>
        </div>

        {loading ? (
          <div className="py-8 text-center text-gray-500 text-sm"><Spinner /></div>
        ) : steps.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">No steps yet. Add one below.</div>
        ) : (
          <div className="space-y-3">
            {steps.map((step, stepIndex) => {
              const isOpen = expanded.has(step.key);
              return (
                <div key={step.key} className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveStep(stepIndex, -1)}
                        disabled={stepIndex === 0}
                        title="Move up"
                        className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStep(stepIndex, 1)}
                        disabled={stepIndex === steps.length - 1}
                        title="Move down"
                        className="px-2 py-0.5 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded text-xs disabled:opacity-30"
                      >
                        ↓
                      </button>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] text-gray-500">Key:</span>
                        <input
                          value={step.key}
                          onChange={(e) => updateStep(stepIndex, { key: e.target.value })}
                          className="px-2 py-0.5 bg-gray-900 border border-gray-700 rounded text-gray-300 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <input
                          value={step.label}
                          onChange={(e) => updateStep(stepIndex, { label: e.target.value })}
                          placeholder="Step label (internal/short)"
                          className={inputCls}
                        />
                        <input
                          value={step.title ?? ""}
                          onChange={(e) => updateStep(stepIndex, { title: e.target.value })}
                          placeholder="Public Title (e.g. Business Info)"
                          className={inputCls}
                        />
                        <input
                          value={step.description ?? ""}
                          onChange={(e) => updateStep(stepIndex, { description: e.target.value })}
                          placeholder="Public Description..."
                          className={inputCls}
                        />
                      </div>
                    </div>

                    <label className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={step.required}
                        onChange={(e) => {
                          const required = e.target.checked;
                          updateStep(stepIndex, {
                            required,
                            fields: step.fields.map((f) => ({ ...f, required })),
                          });
                        }}
                        className="accent-indigo-500"
                      />
                      Required
                    </label>

                    <button
                      type="button"
                      onClick={() => toggleExpand(step.key)}
                      className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-xs transition-colors"
                    >
                      {isOpen ? "Hide fields" : `Fields (${step.fields.length})`}
                    </button>

                    <button
                      type="button"
                      onClick={() => removeStep(stepIndex)}
                      className="px-3 py-1.5 bg-red-900/30 hover:bg-red-900/60 text-red-400 rounded-lg text-xs transition-colors"
                    >
                      Remove step
                    </button>
                  </div>

                  {isOpen && (
                    <div className="mt-4 pl-12">
                      <AdminFieldList
                        fields={step.fields}
                        onChange={(fields) => {
                          const hasRequiredField = fields.some((f) => f.required);
                          updateStep(stepIndex, {
                            fields,
                            required: hasRequiredField,
                          });
                        }}
                        optionTypes={optionTypes}
                        deriveKeyFromLabel={deriveKeyFromLabel}
                        inputCls={inputCls}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={stepsEndRef} className="h-1" />
          </div>
        )}

        {/* Add step */}
        <div className="mt-5 pt-4 border-t border-gray-800">
          <h3 className="text-xs font-semibold text-gray-400 mb-2">Add new step</h3>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[10px] text-gray-500 mb-1">Step key (slug)</label>
              <input
                value={newStepKey}
                onChange={(e) => setNewStepKey(e.target.value)}
                placeholder="e.g. contact_details"
                className={inputCls}
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[10px] text-gray-500 mb-1">Label</label>
              <input
                value={newStepLabel}
                onChange={(e) => setNewStepLabel(e.target.value)}
                placeholder="Contact details"
                className={inputCls}
              />
            </div>
            <button
              type="button"
              onClick={handleAddStep}
              disabled={!newStepKey.trim() || !newStepLabel.trim()}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              + Add step
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-8">
        <button
          onClick={() => void handleSave()}
          disabled={saving || loading}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={() => setShowPreview(true)}
          className="px-5 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl text-sm font-medium transition-colors"
        >
          Preview Flow
        </button>
      </div>

      {/* Live Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-3xl max-h-[90vh] shadow-2xl relative flex flex-col shadow-black/50">
            
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-gray-900/95 backdrop-blur rounded-t-2xl">
              <div>
                <h2 className="text-lg font-semibold text-white">Live Preview</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Currently viewing: <span className="font-medium text-gray-300">{countryLabel(selectedCountry, countryName(selectedCountry))}</span>
                </p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-gray-600"
                title="Close preview"
              >
                ✕
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto">
              
              {/* Fake Stepper */}
              {steps.length > 0 && (
                <div className="mb-8 px-4">
                  <div className="flex items-center justify-between">
                    {steps.map((s, i) => {
                      const active = i === previewStepIndex;
                      const done = i < previewStepIndex;
                      return (
                        <div key={s.key} className="flex flex-1 items-center cursor-pointer" onClick={() => setPreviewStepIndex(i)}>
                          <div className="flex flex-col items-center">
                            <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all ${
                              done    ? "border-indigo-500 bg-indigo-500 text-white" :
                              active  ? "border-indigo-500 bg-indigo-500/10 text-indigo-400" :
                                        "border-gray-700 bg-gray-800 text-gray-500"
                            }`}>
                              {done ? "✓" : i + 1}
                            </div>
                            <span className={`mt-2 hidden text-[10px] sm:block ${active ? "font-semibold text-indigo-400" : done ? "text-indigo-300" : "text-gray-500"}`}>
                              {s.label}
                            </span>
                          </div>
                          {i < steps.length - 1 && (
                            <div className={`mx-2 h-0.5 flex-1 transition-all ${done ? "bg-indigo-500" : "bg-gray-700"}`} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Step Form */}
              {steps.length > 0 && previewStepIndex < steps.length && (
                <div className="glass-panel p-6 shadow-lg max-w-2xl mx-auto">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-ink">{steps[previewStepIndex].title || steps[previewStepIndex].label}</h2>
                    {steps[previewStepIndex].description && <p className="mt-1 text-sm text-ink-muted">{steps[previewStepIndex].description}</p>}
                  </div>
                  
                  <div className="space-y-5">
                    {steps[previewStepIndex].fields.length === 0 ? (
                      <p className="text-xs text-ink-muted italic">No fields in this step.</p>
                    ) : (
                      steps[previewStepIndex].fields.map((f) => (
                        <div key={f.key} className="transition-all hover:bg-black/5 p-3 -mx-3 rounded-xl border border-transparent hover:border-black/10">
                          <FlowFieldRenderer
                            field={f}
                            value={""}
                            onChange={() => {}}
                          />
                        </div>
                      ))
                    )}
                  </div>

                  {/* Fake Step Footer */}
                  <div className="mt-8 flex items-center justify-between border-t border-border pt-5">
                    {!steps[previewStepIndex].required ? (
                      <button type="button" onClick={() => setPreviewStepIndex(Math.min(steps.length-1, previewStepIndex+1))} className="text-sm font-medium text-ink-muted hover:text-ink transition-colors">
                        Skip
                      </button>
                    ) : (
                      <div />
                    )}
                    <button type="button" onClick={() => setPreviewStepIndex(Math.min(steps.length-1, previewStepIndex+1))} className="rounded-xl bg-brand px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand">
                      {previewStepIndex === steps.length - 1 ? "Complete Profile" : "Next"}
                    </button>
                  </div>
                </div>
              )}

              {steps.length === 0 && (
                <div className="text-center py-10 text-gray-500 text-sm">
                  No steps configured for this flow yet.
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-800 bg-gray-900 p-4 rounded-b-2xl flex justify-end">
              <button
                onClick={() => setShowPreview(false)}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Done
              </button>
            </div>

          </div>
        </div>
      )}


      {confirmDelete && (
        <ConfirmModal
          title="Delete country config"
          description={`Remove the override for ${countryLabel(
            selectedCountry,
            countryName(selectedCountry),
          )}? This role+country will fall back to the Default config.`}
          items={["The config document will be permanently removed"]}
          confirmLabel="Delete"
          onConfirm={() => void handleDelete()}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

export default function AdminOnboardingPage() {
  const [tab, setTab] = useState<Tab>("seller");

  return (
    <div>
      <AdminPageHeader
        title="Onboarding"
        subtitle={
          <>
            Configure onboarding steps and fields per country for each role, and manage the option-value sets
            those fields reference.
          </>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-800">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
              tab === t.key
                ? "border-indigo-500 text-white"
                : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "options" ? (
        <AdminOnboardingOptionsPage />
      ) : tab === "seller_types" ? (
        <AdminSellerTypesPage />
      ) : tab === "field_types" ? (
        <FieldTypesReferencePage />
      ) : (
        <FlowBuilder key={tab} role={tab} />
      )}
    </div>
  );
}
